import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup 
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  prjDeptHeaderTyps,
  prjOrderHeaderTyps,
  prjRealSalesHeaderTyps,
  prjSalesHeaderTyps,
  prjStatHeaderTyps,
  prjDeptHeaders,
  prjTargetRateHeaderTyps,
  prjExTargetRateHeaderTyps,
} from '../../utils/forecastStaticData';
import SalesStaticsHeadquerterRows from './SalesStaticsHeadquerterRows';

const useStyles = makeStyles({
  table: {
    '& .MuiTableCell-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
      // [핵심] \n 문자를 줄바꿈으로 렌더링하고, 셀 너비를 넘치면 자동 줄바꿈
      whiteSpace: 'pre-wrap', 
      wordBreak: 'break-all', 
      overflow: 'visible',
      textOverflow: 'clip',
      padding: '6px 4px',
    },
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
    },
  },
});


const SalesStaticsHeadquarterTable = ({ 
  data, 
  tableRef, 
  selectPrjTyp, 
  strMonth, 
  endMonth
}) => {
  const classes = useStyles();
  const [showKorean, setShowKorean] = useState(false);

  const getBgColor = (id) => {
    if (id?.includes('STAT')) return '#b0deff';
    if (id === 'OA') return '#e3f2fd';
    if (id === 'HM') return '#fff3e0';
    if (id === 'ES') return '#e8f5e9';
    return '#f5f5f5';
  };

  const allHeaders = prjDeptHeaders(
    strMonth.split('-')[0],
    endMonth.split('-')[0],
    '매출',
    strMonth.split('-')[1],
    endMonth.split('-')[1]
  );
  
  const filteredHeaders = allHeaders.filter((header) => 
    header.id.includes('STAT') ? selectPrjTyp === 'A' : true
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, px: 1 }}>
        <ToggleButtonGroup
          value={showKorean}
          exclusive
          onChange={(e, nextValue) => { if (nextValue !== null) setShowKorean(nextValue); }}
          size="small"
          sx={{
            height: '32px',
            '& .MuiToggleButton-root': {
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#455a64',
              px: 1.5,
              border: '1px solid #cfd8dc', 
              '&.Mui-selected': {
                backgroundColor: '#1967d2',
                color: 'white',
              },
            },
          }}
        >
          <ToggleButton value={false}>숫자 적용</ToggleButton>
          <ToggleButton value={true}>한글 적용</ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="caption" sx={{ color: '#455a64', fontWeight: 700, fontSize: '0.75rem', marginLeft: 'auto' }}>
          (단위: {showKorean ? '한글 혼용' : '원'})
        </Typography>
      </Box>
   
      <Paper sx={{ my: 1, width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ width: '100%', maxHeight: '70vh', overflowX: 'auto' }}>
          <Table 
            size="small" 
            className={classes.table} 
            ref={tableRef} 
            stickyHeader
            sx={{ 
              tableLayout: 'fixed', // 너비 고정 모드
              width: '100%',        // 화면 꽉 채우기
              minWidth: '1100px'    // 최소 가로폭 유지
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell 
                  align="center" 
                  rowSpan={2} 
                  sx={{ 
                    bgcolor: '#f5f5f5', 
                    width: '120px',    // 120px로 통일
                    minWidth: '120px', 
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                  }}
                >
                  본부
                </TableCell>
                {filteredHeaders.map((header) => (
                  <TableCell
                    key={header.val}
                    align="center"
                    colSpan={header.id === 'OA' ? 7 : 2}
                    sx={{ 
                      bgcolor: getBgColor(header.id), 
                      fontWeight: 800, 
                      fontSize: '0.9rem',
                      width: 'auto' // 데이터 영역 균등 배분
                    }}
                  >
                    {header.name}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {filteredHeaders.map((header) => {
                  const headerTyp =
                    header?.id === 'EXSTATRATE' ? prjExTargetRateHeaderTyps :
                    header?.id === 'STATRATE' ? prjTargetRateHeaderTyps : 
                    header?.id?.includes('STAT') ? prjStatHeaderTyps : 
                    header?.id === 'OA' ? prjRealSalesHeaderTyps : 
                    header?.id === 'HM' ? prjDeptHeaderTyps : 
                    prjSalesHeaderTyps;

                  return headerTyp.map((typ, index) => (
                    <TableCell 
                      key={`${header.id}-${index}`}
                      align="center" 
                      sx={{ 
                        bgcolor: getBgColor(header.id),
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        width: 'auto'
                      }}
                    >
                      {typ.name}
                    </TableCell>
                  ));  
              })}
              </TableRow>
            </TableHead>

            <TableBody>
              {!data || data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={50} align="center" sx={{ py: 5 }}>
                    데이터가 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                <SalesStaticsHeadquerterRows
                  useKorean={showKorean}
                  headerRows={filteredHeaders}
                  data={data}
                  selectPrjTyp={selectPrjTyp}
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default SalesStaticsHeadquarterTable;
